import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, googleProvider } from '../firebase/firebase.config';
import { signInWithPopup, onAuthStateChanged, signOut, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { buildApiUrl } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [initialSetupStatus, setInitialSetupStatus] = useState(null);

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      console.log('🔐 Starting Google sign-in with popup...');
      // Ensure persistence is set to LOCAL
      await setPersistence(auth, browserLocalPersistence);
      console.log('✅ Persistence set to LOCAL');
      // Use popup method for better reliability on localhost
      const result = await signInWithPopup(auth, googleProvider);
      console.log('✅ Sign-in successful:', result.user.email);
      
      // Get token and send to backend - WAIT for this to complete
      const idToken = await result.user.getIdToken();
      console.log('🔑 Got Firebase ID token');
      
      // Send to backend and wait for JWT to be stored
      const backendData = await sendTokenToBackend(idToken);
      
      if (backendData && backendData.token) {
        console.log('✅ JWT token stored in localStorage');
      } else {
        console.warn('⚠️ Backend did not return JWT token');
      }
      
      // Track login for simple view access
      try {
        await fetch(buildApiUrl('/api/simplified/track-login'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${backendData?.token || idToken}`,
            'Content-Type': 'application/json',
          },
        });
        console.log('✅ Login tracked for simple view');
      } catch (err) {
        console.warn('⚠️ Failed to track login:', err);
        // Don't block login if tracking fails
      }
      
      // Update state after everything is ready
      setToken(idToken);
      setUser(result.user);
      console.log('✅ Auth state updated, ready to navigate');
      
      return result;
    } catch (error) {
      console.error('❌ Error signing in with Google:', error);
      throw error;
    }
  };

  // Send token to backend for verification
  const sendTokenToBackend = async (idToken) => {
    try {
      console.log('📤 Sending Firebase token to backend...');
      const apiUrl = buildApiUrl('/auth/login');
      console.log('🔗 Backend URL:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
      });
      
      console.log('📥 Backend response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Backend authentication failed:', response.status, errorText);
        // Don't throw error - just log warning and continue
        // This allows the app to work even if backend is not accessible
        return null;
      }
      
      const data = await response.json();
      console.log('✅ Backend returned data:', { hasToken: !!data.token });
      
      if (data.token) {
        localStorage.setItem('jwtToken', data.token);
        console.log('💾 JWT token saved to localStorage');
      } else {
        console.error('❌ Backend response did not include token');
      }
      
      return data;
    } catch (error) {
      console.error('❌ Error sending token to backend:', error);
      // Don't throw error - just log warning and continue
      // This allows the app to work even if backend is not accessible
      return null;
    }
  };

  // Sign out
  const signOutUser = async () => {
    try {
      // Note: No need to reset session on logout anymore
      // Login count is persistent across sessions
      
      await signOut(auth);
      setToken(null);
      localStorage.removeItem('jwtToken');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  // Get JWT token for API calls
  const getAuthHeaders = () => {
    const jwtToken = localStorage.getItem('jwtToken');
    if (!jwtToken) {
      console.warn('⚠️ No JWT token found in localStorage when calling getAuthHeaders');
    } else {
      console.log('✅ JWT token found in localStorage');
    }
    return {
      'Authorization': `Bearer ${jwtToken}`,
      'Content-Type': 'application/json',
    };
  };

  // Save user data to Firebase
  const saveUserData = async (data) => {
    try {
      const response = await fetch(buildApiUrl('/auth/user-data'), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error('Failed to save user data');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error saving user data:', error);
      throw error;
    }
  };

  // Get user data from Firebase
  const getUserData = async () => {
    try {
      console.log('📥 Fetching user data from backend...');
      const apiUrl = buildApiUrl('/auth/user-data');
      console.log('🔗 API URL:', apiUrl);
      
      const headers = getAuthHeaders();
      console.log('📋 Request headers:', { 
        hasAuth: !!headers.Authorization,
        authLength: headers.Authorization?.length 
      });
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: headers,
      });
      
      console.log('📥 Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Backend error response:', response.status, errorText);
        
        // If 404, user data doesn't exist yet - return null instead of throwing
        if (response.status === 404) {
          console.log('ℹ️ User data not found (404) - this is normal for new users');
          return null;
        }
        
        throw new Error(`Failed to get user data: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      console.log('✅ User data loaded successfully');
      return data;
    } catch (error) {
      console.error('❌ Error getting user data:', error);
      throw error;
    }
  };

  // Update user data
  const updateUserData = async (data) => {
    try {
      const response = await fetch(buildApiUrl('/auth/user-data/update'), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update user data');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating user data:', error);
      throw error;
    }
  };

  // Save onboarding data
  const saveOnboardingData = async (data) => {
    try {
      const response = await fetch(buildApiUrl('/auth/onboarding'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error('Failed to save onboarding data');
      }
      
      setShowOnboarding(false);
      return await response.json();
    } catch (error) {
      console.error('Error saving onboarding data:', error);
      throw error;
    }
  };

  // Check if user needs onboarding
  const checkOnboardingStatus = async () => {
    if (!user) return;
    
    try {
      const response = await fetch(buildApiUrl('/auth/onboarding/status'), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      
      if (response.ok) {
        const data = await response.json();
        setShowOnboarding(data.needsOnboarding);
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    }
  };

  // Check initial setup status
  const checkInitialSetupStatus = async () => {
    if (!user) return null;
    
    try {
      const response = await fetch(buildApiUrl('/auth/initial-setup/status'), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      
      if (response.ok) {
        const data = await response.json();
        setInitialSetupStatus(data);
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error checking initial setup status:', error);
      return null;
    }
  };

  // Save initial setup data
  const saveInitialSetup = async (setupData) => {
    try {
      const response = await fetch(buildApiUrl('/auth/initial-setup'), {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(setupData),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to save initial setup data');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error saving initial setup data:', error);
      throw error;
    }
  };

  // Save mode selection
  const saveModeSelection = async (selectedMode) => {
    try {
      const response = await fetch(buildApiUrl('/auth/initial-setup/mode-selection'), {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ selectedMode }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to save mode selection');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error saving mode selection:', error);
      throw error;
    }
  };

  useEffect(() => {
    let mounted = true;

    // Listen for auth state changes - this will fire when persisted auth is loaded or on page refresh
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      try {
        console.log('🔔 Auth state changed:', u ? `✅ ${u.email}` : '❌ No user');
        if (mounted) {
          setUser(u);
          if (u) {
            // Get Firebase ID token
            const idToken = await u.getIdToken();
            setToken(idToken);
            // Try to send to backend, but don't fail if it doesn't work
            await sendTokenToBackend(idToken);
            console.log('✅ User token obtained and sent to backend');
          }
        }
      } catch (error) {
        console.error('❌ Error getting user token:', error);
      } finally {
        if (mounted) {
          console.log('✅ Setting loading to false');
          setLoading(false);
        }
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  // Check onboarding status when user changes
  useEffect(() => {
    if (user && !loading) {
      checkOnboardingStatus();
      checkInitialSetupStatus();
    }
  }, [user, loading]);

  const value = {
    user,
    token,
    loading,
    showOnboarding,
    signInWithGoogle,
    signOutUser,
    getAuthHeaders,
    saveUserData,
    getUserData,
    updateUserData,
    saveOnboardingData,
    setShowOnboarding,
    initialSetupStatus,
    checkInitialSetupStatus,
    saveInitialSetup,
    saveModeSelection,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 